import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Divider,
  HStack,
  Skeleton,
  SkeletonText,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useState } from "react";
import { ChevronUpIcon } from "@chakra-ui/icons";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { PageContent } from "../../components/PageContent/PageContent";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { OdmaUrlType } from "../../enums/OdmaUrlType";
import { useAdminApiUpdateRequest } from "../../hooks/useApiUpdatedRequest";
import { useToast } from "../../hooks/useToast";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { CopyText } from "../../components/Utilities/CopyText";
import { PreviewButton } from "../../components/Utilities/PreviewButton";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { BusinessMenu } from "../../components/Business/BusinessMenu";
import { MemberSelfServiceConfigurationForm } from "../../forms/MemberSelfServiceConfigurationForm";

interface MemberSelfServiceConfigurationDetailProps {
  create?: boolean;
}

export const MemberSelfServiceConfigurationDetail = (
  props: MemberSelfServiceConfigurationDetailProps
) => {
  const [searchParams] = useSearchParams();
  const { businessId, memberSelfServiceConfigurationId } = useParams();
  const [configuration, setConfiguration] =
    useState<CherryHubAdminApi.MemberSelfServiceConfigurationUpdate | null>();
  const [editMode, setEditMode] = useState<boolean>(
    props.create ??
      (searchParams.get("edit") !== null &&
      (searchParams.get("edit") === "" || searchParams.get("edit") === "true")
        ? true
        : false)
  );
  const navigate = useNavigate();
  const { showErrorToast, showSuccessToast } = useToast();
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();

  const {
    data: configurationDoc,
    isLoading,
    error: configurationError,
  } = useAdminApiRequest(
    (apiClient) => {
      if (!businessId || !memberSelfServiceConfigurationId) {
        return null;
      }
      return apiClient.getMemberSelfServiceConfiguration(
        businessId,
        memberSelfServiceConfigurationId
      );
    },
    [businessId, memberSelfServiceConfigurationId]
  );

  const { data: odmaUrl } = useAdminApiRequest(
    (apiClient) => {
      if (!businessId || !memberSelfServiceConfigurationId || props.create) {
        return null;
      }
      return apiClient.getOdmaUrl(
        businessId,
        OdmaUrlType.MemberSelfService,
        memberSelfServiceConfigurationId
      );
    },
    [businessId, memberSelfServiceConfigurationId]
  );

  useEffect(() => {
    setConfiguration(configurationDoc);
  }, [configurationDoc]);

  if (configurationError)
    return <ErrorMessage>{configurationError}</ErrorMessage>;

  const onSuccess = useCallback(
    (updated: CherryHubAdminApi.MemberSelfServiceConfigurationUpdate) => {
      setConfiguration(updated);
      setEditMode(false);
      if (props.create) {
        showSuccessToast("Member Self-Service Configuration created.");
        navigate(
          `/admin/businesses/${businessId}/member-self-service-configurations/${
            (updated as any).Meta?.Id
          }`
        );
      } else {
        showSuccessToast("Member Self-Service Configuration updated.");
      }
    },
    [showSuccessToast, configuration]
  );

  const onFailure = useCallback(
    (message?: string) => {
      showErrorToast(
        message ?? "Member Self-Service Configuration update failed."
      );
    },
    [showErrorToast]
  );

  const onCancel = () => {
    props.create
      ? navigate(
          `/admin/businesses/${businessId}/member-self-service-configurations`
        )
      : setEditMode(false);
  };

  const configAsAny = configuration as any;
  const friendlyName = configAsAny?.FriendlyName ?? configuration?.FriendlyName;
  const configNo = configAsAny?.MemberSelfServiceConfigurationNo;

  return (
    <>
      <PageHeading>
        <PageHeading.Title>
          {props.create ? "Create" : "Edit"} Member Self-Service Configuration
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" w="100%">
          <BusinessBreadcrumbs variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${businessId}/member-self-service-configurations`}
              >
                Member Self-Service Configurations
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${businessId}/member-self-service-configurations/${
                  props.create ? "new" : memberSelfServiceConfigurationId
                }`}
              >
                {props.create ? "New" : friendlyName}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>

          {(isLoading || !configuration) && !props.create && (
            <VStack
              w="100%"
              p={6}
              boxShadow="lg"
              alignItems="flex-start"
              spacing={4}
            >
              <HStack w="100%" justifyContent="flex-start" spacing={4}>
                <Box flexGrow={1}>
                  <Skeleton w="320px" height="40px" />
                </Box>
                {!editMode && <Skeleton w="108px" height="40px" />}
                <Skeleton w="92px" height="40px" />
                <Skeleton w="80px" height="40px" />
              </HStack>
              <VStack w="100%" spacing={6} alignItems="flex-start">
                <SkeletonText w="50%" mt="4" noOfLines={7} spacing="4" />
                <Skeleton w="100%" height="40px" />
                <SkeletonText w="100%" mt="4" noOfLines={5} spacing="4" />
              </VStack>
            </VStack>
          )}

          {!isLoading && editMode && (
            <MemberSelfServiceConfigurationForm
              businessId={businessId!}
              configurationId={memberSelfServiceConfigurationId}
              configuration={configuration}
              onSuccess={onSuccess}
              onFailure={onFailure}
              onCancel={onCancel}
            />
          )}

          {!isLoading && !editMode && configuration && (
            <>
              <HStack w="100%">
                <Box flexGrow={2} pb={4}>
                  <CopyText
                    text={memberSelfServiceConfigurationId!}
                    hover
                    adjustMargin
                  >
                    <Text fontSize="md" color="cherryUi.600">
                      {memberSelfServiceConfigurationId}
                      {configNo != null && ` / #${configNo}`}
                    </Text>
                  </CopyText>
                  <Text fontSize="xl" color="cherryUi.600">
                    <b>{friendlyName}</b>
                  </Text>
                </Box>

                {odmaUrl?.Value != null && (
                  <Box>
                    <PreviewButton
                      url={odmaUrl.Value as string}
                      variant="cherryButton"
                    >
                      Open
                    </PreviewButton>
                  </Box>
                )}

                {odmaUrl?.Value != null && (
                  <Box>
                    <CopyText
                      text={odmaUrl.Value as string}
                      variant="cherryButton"
                    >
                      Copy Url
                    </CopyText>
                  </Box>
                )}

                <Box>
                  <BusinessMenu
                    businessId={businessId!}
                    onClick={() => setEditMode(true)}
                  />
                </Box>
              </HStack>

              <VStack w="100%" alignItems="flex-start" spacing={2} pb={4}>
                <ViewControl
                  label="Enabled"
                  value={configAsAny?.IsEnabled ? "Yes" : "No"}
                />
                <ViewControl
                  label="Integration ID"
                  value={configAsAny?.IntegrationId ?? "-"}
                />
              </VStack>

              <Accordion
                w="100%"
                defaultIndex={[0, 1, 2, 3]}
                allowToggle
                allowMultiple
                variant="section"
              >
                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>General</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel>
                        <VStack w="100%" alignItems="flex-start">
                          <ViewControl
                            label="Language"
                            value={configuration?.Language?.LanguageCode ?? "-"}
                          />
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />
                          <ViewControl
                            label="OTP Method"
                            value={configuration?.OTPMethod ?? "-"}
                          />
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />
                          <ViewControl
                            label="Return Preferred OTP Method Only"
                            value={
                              configuration?.ReturnPreferredOTPMethodOnly
                                ? "Yes"
                                : "No"
                            }
                          />
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />
                          <ViewControl
                            label="OTP Valid Time Period"
                            value={
                              configuration?.OTPValidTimePeriod
                                ? `${configuration.OTPValidTimePeriod.Quantity} ${configuration.OTPValidTimePeriod.Period}`
                                : "-"
                            }
                          />
                        </VStack>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>

                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Page Content</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel>
                        <VStack w="100%" alignItems="flex-start">
                          <ViewControl
                            label="Page Title"
                            value={configuration?.PageTitle ?? "-"}
                          />
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />
                          <ViewControl
                            label="Logo URL"
                            value={configuration?.LogoFileUrl ?? "-"}
                          />
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />
                          <ViewControl label="Privacy Policy URL">
                            <Box flex={3}>
                              {configuration?.PrivacyPolicyUrl ? (
                                <PreviewButton
                                  url={configuration.PrivacyPolicyUrl}
                                  adjustMargin
                                >
                                  {configuration.PrivacyPolicyUrl}
                                </PreviewButton>
                              ) : (
                                "-"
                              )}
                            </Box>
                          </ViewControl>
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />
                          <ViewControl label="Terms & Conditions URL">
                            <Box flex={3}>
                              {configuration?.TermsConditionsUrl ? (
                                <PreviewButton
                                  url={configuration.TermsConditionsUrl}
                                  adjustMargin
                                >
                                  {configuration.TermsConditionsUrl}
                                </PreviewButton>
                              ) : (
                                "-"
                              )}
                            </Box>
                          </ViewControl>
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />
                          <ViewControl
                            label="Geo Country Filter"
                            value={
                              configuration?.GeoSuggestCountryFilter?.join(
                                ", "
                              ) ?? "-"
                            }
                          />
                        </VStack>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>

                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Form Fields</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel>
                        {configuration?.FormFields &&
                        configuration.FormFields.length > 0 ? (
                          <Table size="sm">
                            <Thead>
                              <Tr>
                                <Th>Identifier</Th>
                                <Th>Label</Th>
                                <Th>Verification Required</Th>
                                <Th>Obfuscated</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {configuration.FormFields.filter(
                                (field) => field.IsVisible
                              ).map((field) => (
                                <Tr key={field.Identifier}>
                                  <Td>
                                    <Tag size="sm">{field.Identifier}</Tag>
                                  </Td>
                                  <Td>{field.Name}</Td>
                                  <Td>
                                    {field.VerificationRequired ? "Yes" : "No"}
                                  </Td>
                                  <Td>
                                    {field.IsObfuscated !== false
                                      ? "Yes"
                                      : "No"}
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        ) : (
                          <Text color="gray.500" fontSize="sm">
                            No form fields configured.
                          </Text>
                        )}
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>

                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Advanced</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={4}>
                        <VStack w="100%" alignItems="flex-start">
                          <Box
                            width="100%"
                            borderWidth="1px"
                            borderRadius="lg"
                            boxShadow="lg"
                            overflowX="auto"
                            padding={6}
                            flex={3}
                          >
                            {configuration?.ThemeData ? (
                              <pre>
                                {JSON.stringify(
                                  configuration.ThemeData,
                                  null,
                                  2
                                )}
                              </pre>
                            ) : (
                              <Text color="gray.500" fontSize="sm">
                                No theme data configured.
                              </Text>
                            )}
                          </Box>
                        </VStack>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
              </Accordion>
            </>
          )}
          {confirmationElement}
        </VStack>
      </PageContent>
    </>
  );
};
